import React, { useMemo, useState } from 'react';
import { displayCallOutcome, normalizeCallOutcomeSlug } from '../../utils/callOutcomeAnalytics';

const SLUG_STYLE = {
  reached_appt: { color: '#16a34a', bg: 'rgba(22,163,74,0.1)', border: 'rgba(22,163,74,0.25)' },
  gatekeeper: { color: '#92400e', bg: 'rgba(146,64,14,0.08)', border: 'rgba(146,64,14,0.2)' },
  decision_maker: { color: '#0f766e', bg: 'rgba(15,118,110,0.1)', border: 'rgba(15,118,110,0.22)' },
  reached_followup: { color: '#0f8a53', bg: 'rgba(15,138,83,0.08)', border: 'rgba(15,138,83,0.2)' },
  reached_no: { color: '#dc2626', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.2)' },
  callback: { color: '#d97706', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.2)' },
  unknown: { color: 'var(--text2)', bg: 'var(--bg4)', border: 'var(--border)' },
};

function fmt(ts) {
  if (!ts) return '–';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function ActivityDrilldownModal({ open, title, subtitle, calls = [], onClose, onOpenLead }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return calls;
    return calls.filter(c => {
      const n = (c.lead?.name || c.title || '').toLowerCase();
      const co = (c.lead?.company || '').toLowerCase();
      const note = (c.note || '').toLowerCase();
      const by = (c.by || '').toLowerCase();
      return n.includes(q) || co.includes(q) || note.includes(q) || by.includes(q);
    });
  }, [calls, search]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 'min(520px, 100vw)', maxWidth: '100%',
        background: 'var(--bg2)', borderLeft: '1px solid var(--border)',
        boxShadow: '-12px 0 48px rgba(15,30,60,0.2)',
        display: 'flex', flexDirection: 'column', height: '100%',
        fontFamily: 'var(--font)',
      }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
              {subtitle && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{subtitle}</div>}
              <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, marginTop: 6 }}>{filtered.length} Anrufe · Klick öffnet Lead</div>
            </div>
            <button type="button" onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', cursor: 'pointer', fontSize: 18, color: 'var(--text2)' }}>×</button>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Lead, Firma, Notiz…"
            style={{ width: '100%', marginTop: 12, height: 36, borderRadius: 8, border: '1.5px solid var(--border)', padding: '0 12px', fontSize: 13, boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text)' }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 13 }}>Keine Anrufe in dieser Auswahl.</div>
          ) : (
            filtered.map(call => {
              const slug = normalizeCallOutcomeSlug(call);
              const oc = SLUG_STYLE[slug] || SLUG_STYLE.unknown;
              return (
                <button
                  key={`${call.leadId}-${call.id}`}
                  type="button"
                  onClick={() => call.lead && onOpenLead?.(call.lead)}
                  style={{
                    width: '100%', textAlign: 'left', marginBottom: 8, padding: '12px 14px',
                    borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)',
                    cursor: call.lead ? 'pointer' : 'default',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{call.lead?.name || call.title || 'Unbekannt'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{call.lead?.company || '–'} · {call.by || '–'}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: oc.color, background: oc.bg, border: `1px solid ${oc.border}`, borderRadius: 6, padding: '3px 8px', flexShrink: 0 }}>
                      {displayCallOutcome(call)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{fmt(call.ts || call.date)}{call.duration ? ` · ${call.duration}` : ''}</div>
                  {call.note && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6, lineHeight: 1.45 }}>{call.note}</div>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
